import React from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { TitleSection, Breadcrumb, H1, Section } from '@shortlyster/ui-kit';
import { Box } from '@compono/ui';
import { useParams } from 'react-router-dom';
import Link from '../../../components/Link';
import { LegacyPage } from 'components/LegacyPage';
import {
  OrganisationQuery,
  organisationQuery,
  CreateSSOConnection,
  GetSsoConfig,
  SsoConfig,
} from '../queries';
import OrganisationSsoSettingsForm, { NewSso } from './OrganisationSsoSettingsForm';
import { showAlert } from 'src/components/AlertUtils';
import { useApolloClients } from 'src/lib/auth/AuthorizedApolloProvider';
import history from 'lib/history';
import Container from 'src/components/Container';

const CreateOrganisationSso: React.FC = () => {
  const { id: orgId } = useParams<{ id: string }>();
  const { federationGatewayClient } = useApolloClients()!;

  const { data: orgQueryData, loading: orgQueryLoading } = useQuery<OrganisationQuery>(
    organisationQuery,
    {
      variables: { id: orgId },
      fetchPolicy: 'cache-and-network',
    }
  );
  const { data: ssoConfigData, loading: ssoConfigLoading } = useQuery<SsoConfig>(GetSsoConfig, {
    client: federationGatewayClient,
    fetchPolicy: 'cache-and-network',
  });
  const [createSsoConnection, { loading: createLoading, error: createError }] = useMutation(
    CreateSSOConnection,
    {
      client: federationGatewayClient,
    }
  );

  if (orgQueryLoading || ssoConfigLoading) return <p>Loading...</p>;
  const organisation = orgQueryData?.organisations[0];
  const organisationId = organisation && organisation.id;
  const onSubmit = async (formData: NewSso) => {
    const payload: any = {
      organisationId,
      enabled: formData.enabled,
      name: formData.name,
      type: formData.type,
      encryptedResponses: formData.encryptedResponses,
      autoSignup: formData.autoSignup,
      replaceLoginPage: formData.replaceLoginPage,
    };

    if (formData.metadataFile) {
      payload.metadataFile = formData.metadataFile;
    } else if (formData.metadataUrl) {
      payload.metadataUrl = formData.metadataUrl;
    }
    try {
      const { data } = await createSsoConnection({
        variables: { input: payload },
      });

      const newSSO = data?.createSSOConnection;
      if (!newSSO?.id) throw new Error('No SSO ID returned');
      showAlert({
        title: 'SSO settings created',
        content: 'SSO settings have been created.',
        alertType: 'success',
      });
      history.changeDomainTo(`/organisations/${orgId}/sso/${newSSO.id}`);
    } catch (e: any) {
      showAlert({
        title: 'Error while creating SSO settings',
        content: 'SSO settings could not be created.',
        alertType: 'error',
      });
    }
  };
  return (
    <Container>
      <TitleSection>
        <Breadcrumb>
          <Link to="/organisations">Organisations</Link>
          <Link to={`/organisations/${orgId}/sso`}>{organisation?.name}</Link>
          <Link to="" aria-current={true}>
            Create SSO Connection
          </Link>
        </Breadcrumb>
        <H1>Create SSO Connection</H1>
      </TitleSection>
      <Section>
        <Box
          sx={{
            padding: 4,
            width: ['100%'],
          }}
        >
          <OrganisationSsoSettingsForm
            onSubmit={onSubmit}
            defaultValue={ssoConfigData.ssoConfig}
            saving={createLoading}
            orgId={orgId}
          />
        </Box>
      </Section>
    </Container>
  );
};

export default CreateOrganisationSso;
